import React, { useState } from 'react';
import axios from 'axios';
import { X, Star, Send, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewModal = ({ open, onClose }) => {
  const { language, t } = useLanguage();
  const [form, setForm] = useState({ name: '', trip: '', rating: 0, message: '', email: '' });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const reset = () => {
    setForm({ name: '', trip: '', rating: 0, message: '', email: '' });
    setHoverRating(0);
    setDone(false);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.rating < 1) {
      setError(language === 'fr' ? 'Veuillez sélectionner une note' : 'Please select a rating');
      return;
    }
    if (form.message.trim().length < 10) {
      setError(language === 'fr' ? 'Le message doit contenir au moins 10 caractères' : 'Message must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/reviews`, payload);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'envoi' : 'Submission error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={handleClose}
      data-testid="review-modal-backdrop"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-br from-blue-600 to-blue-700 text-white px-6 py-5 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="text-2xl font-bold">{t('reviewModalTitle')}</h3>
            <p className="text-blue-100 text-sm mt-1">{t('reviewModalSubtitle')}</p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            data-testid="review-modal-close-btn"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {done ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-600" size={48} />
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-2">{t('reviewThanksTitle')}</h4>
            <p className="text-gray-600 mb-6">{t('reviewThanksMsg')}</p>
            <button
              onClick={handleClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
              data-testid="review-modal-close-success-btn"
            >
              {t('close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('reviewRating')} <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    onMouseEnter={() => setHoverRating(n)}
                    className="transition-transform hover:scale-110"
                    data-testid={`review-star-${n}`}
                    aria-label={`${n} étoiles`}
                  >
                    <Star
                      size={36}
                      className={
                        (hoverRating || form.rating) >= n
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-300'
                      }
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('reviewName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={language === 'fr' ? 'Jean Mukendi' : 'John Doe'}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                data-testid="review-input-name"
                maxLength={80}
              />
            </div>

            {/* Trip */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('reviewTrip')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.trip}
                onChange={(e) => setForm({ ...form, trip: e.target.value })}
                placeholder={language === 'fr' ? 'Voyage à Dubaï' : 'Trip to Dubai'}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                data-testid="review-input-trip"
                maxLength={120}
              />
            </div>

            {/* Email (optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('reviewEmail')} <span className="text-gray-400 text-xs font-normal">({t('optional')})</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@exemple.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
                data-testid="review-input-email"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('reviewMessage')} <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={language === 'fr' ? 'Partagez votre expérience...' : 'Share your experience...'}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition resize-none"
                data-testid="review-input-message"
                maxLength={600}
              />
              <div className="text-xs text-gray-400 text-right mt-1">{form.message.length}/600</div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              data-testid="review-submit-btn"
            >
              {submitting ? (t('reviewSubmitting')) : (<>{t('reviewSubmit')} <Send size={18} /></>)}
            </button>
            <p className="text-xs text-gray-500 text-center">{t('reviewModerationNote')}</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReviewModal;
